import { useCallback, useEffect, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import OnboardingLayout from '../../components/OnboardingLayout'
import OnboardingField from '../../components/onboarding/OnboardingField'
import { geocodeAddress, GeocodeUnavailableError } from '../../lib/maps'
import { getCountryLocale } from '../../lib/groceryPrices'
import { clientCountryGuess, clientTimezone } from '../../lib/clientContext'
import { invalidFieldsFromChecks, scrollToFirstFieldError } from '../../lib/onboarding/fieldValidation'
import { ONBOARDING, onboardingChoiceClass, onboardingFieldControlClass } from '../../lib/onboarding/formStyles'
import { ONBOARDING_STEP_COUNT } from '../../lib/onboarding'
import { chainIcon, searchRadiusForCountry, type RetailChain } from '../../lib/retailEngine'
import { fetchRetailChains, registryStoresFromChains } from '../../lib/retailChains'
import { findNearbyStores } from '../../lib/storeLocator'
import type { NearbyStore } from '../../lib/storeTypes'

import type { OnboardingStepProps } from './stepProps'

type LookupState = 'idle' | 'loading' | 'success' | 'error'

// Base weekly budget (EUR) per meal-frequency tier, derived from commodity baseline averages.
// Scale factors for other currencies are approximate mid-market rates vs EUR.
const BUDGET_BASE_EUR: Record<string, number> = { '3_meals': 60, '4_meals': 75, '5_plus_meals': 95 }
const CURRENCY_SCALE: Record<string, number> = {
  EUR: 1, GBP: 0.86, CHF: 0.97, SEK: 11.6, NOK: 11.7, DKK: 7.46,
  ZAR: 20.5, ZWL: 360, USD: 1.08, AUD: 1.65, CAD: 1.47, NZD: 1.79,
}

function suggestBudget(mealFrequency: string, currencyCode: string): number | null {
  const baseEUR = BUDGET_BASE_EUR[mealFrequency]
  const scale = CURRENCY_SCALE[currencyCode]
  if (!baseEUR || !scale) return null
  const raw = baseEUR * scale
  // Round to nearest 5 (or 50 for high-value currencies)
  const step = scale > 50 ? 50 : 5
  return Math.round(raw / step) * step
}

const Step6 = ({ onNext, onPrevious, initialData, stepIndex }: OnboardingStepProps) => {
  const { t } = useTranslation()
  const [formData, setFormData] = useState({
    street_address: (initialData?.street_address as string) || '',
    zipcode: (initialData?.zipcode as string) || '',
    city: (initialData?.city as string) || '',
    shopping_preference: (initialData?.shopping_preference as string) || '',
    weekly_budget_value: (initialData?.weekly_budget_value as string) || '',
  })
  const [error, setError] = useState('')
  const [invalidFields, setInvalidFields] = useState<Set<string>>(new Set())
  const [lookupState, setLookupState] = useState<LookupState>('idle')
  const [lookupError, setLookupError] = useState('')
  const [countryCode, setCountryCode] = useState(String(initialData?.country_code ?? 'AT'))
  const [retailChains, setRetailChains] = useState<RetailChain[]>([])
  const [selectedRetailerId, setSelectedRetailerId] = useState<string>(
    String(initialData?.preferred_retailer_id ?? ''),
  )
  const [nearbyStores, setNearbyStores] = useState<NearbyStore[]>(
    (initialData?.nearby_stores as NearbyStore[]) ?? [],
  )
  const [coords, setCoords] = useState<{ lat: number; lng: number } | null>(
    initialData?.user_lat && initialData?.user_lng
      ? { lat: Number(initialData.user_lat), lng: Number(initialData.user_lng) }
      : null,
  )
  const debounceRef = useRef<ReturnType<typeof setTimeout> | null>(null)

  const runStoreLookup = useCallback(async () => {
    const { street_address, zipcode, city } = formData
    if (!street_address.trim() || !zipcode.trim() || !city.trim()) {
      setLookupState('idle')
      setNearbyStores([])
      setRetailChains([])
      setCoords(null)
      return
    }

    setLookupState('loading')
    setLookupError('')

    try {
      const geo = await geocodeAddress(street_address, city, zipcode)
      const resolvedCountry = geo.country_code
      setCountryCode(resolvedCountry)

      const chains = await fetchRetailChains(resolvedCountry)
      setRetailChains(chains)

      const brandKeys = chains.map((chain) => chain.brand_key)
      const radiusKm = searchRadiusForCountry(resolvedCountry)
      let stores: NearbyStore[] = []
      let placesUnavailable = false
      try {
        stores = await findNearbyStores(geo.lat, geo.lng, {
          countryCode: resolvedCountry,
          allowedBrandKeys: brandKeys.length > 0 ? brandKeys : ['hofer', 'billa', 'lidl_at', 'spar'],
          radiusKm,
        })
      } catch (placesErr) {
        console.warn('Nearby store search unavailable:', placesErr)
        placesUnavailable = true
      }

      if (stores.length === 0 && chains.length > 0) {
        stores = registryStoresFromChains(chains)
        setLookupError(
          placesUnavailable
            ? t('onboarding.location.lookup.placesUnavailable', { country: resolvedCountry })
            : t('onboarding.location.lookup.noStoresInRadius', { radius: radiusKm, country: resolvedCountry }),
        )
      }

      setCoords({ lat: geo.lat, lng: geo.lng })
      setNearbyStores(stores)
      setLookupState('success')

      if (stores.length === 0) {
        setLookupError(t('onboarding.location.lookup.noNetworksRegistered', { country: resolvedCountry }))
      }
    } catch (lookupErr) {
      if (lookupErr instanceof GeocodeUnavailableError) {
        const guessedCountry = String(initialData?.country_code ?? '') || clientCountryGuess()
        setCountryCode(guessedCountry)
        const chains = await fetchRetailChains(guessedCountry)
        if (chains.length > 0) {
          setRetailChains(chains)
          setNearbyStores(registryStoresFromChains(chains))
          setCoords(null)
          setLookupState('success')
          setLookupError(t('onboarding.location.lookup.geocodeUnavailable', { country: guessedCountry }))
          return
        }
      }
      setLookupState('error')
      setCoords(null)
      setNearbyStores([])
      setRetailChains([])
      setLookupError(
        lookupErr instanceof Error ? lookupErr.message : t('onboarding.location.lookup.genericError'),
      )
    }
  }, [formData, initialData?.country_code, t])

  useEffect(() => {
    if (retailChains.length === 1 && !selectedRetailerId) {
      setSelectedRetailerId(retailChains[0].id)
    }
  }, [retailChains, selectedRetailerId])

  useEffect(() => {
    if (debounceRef.current) clearTimeout(debounceRef.current)
    debounceRef.current = setTimeout(() => {
      void runStoreLookup()
    }, 700)
    return () => {
      if (debounceRef.current) clearTimeout(debounceRef.current)
    }
  }, [runStoreLookup])

  const selectedChain = retailChains.find((chain) => chain.id === selectedRetailerId) ?? null
  const locale = getCountryLocale(countryCode)
  const mappedStoreCount = nearbyStores.filter((store) => !store.is_registry_fallback).length

  const mealFrequency = String(initialData?.meal_frequency ?? '')
  const budgetSuggestion = suggestBudget(mealFrequency, locale.currency_code)

  // Auto-fill budget when country resolves and the field is still empty
  useEffect(() => {
    if (!formData.weekly_budget_value && budgetSuggestion) {
      setFormData((prev) => ({ ...prev, weekly_budget_value: String(budgetSuggestion) }))
    }
  }, [countryCode, budgetSuggestion]) // eslint-disable-line react-hooks/exhaustive-deps

  const handleSubmit = () => {
    setError('')

    const checks = {
      street_address: Boolean(formData.street_address.trim()),
      zipcode: Boolean(formData.zipcode.trim()),
      city: Boolean(formData.city.trim()),
      shopping_preference: Boolean(formData.shopping_preference),
      weekly_budget_value: Boolean(formData.weekly_budget_value),
      preferred_retailer_id: Boolean(selectedRetailerId && selectedChain),
    }
    const invalid = invalidFieldsFromChecks(checks)
    if (invalid.size > 0) {
      setInvalidFields(invalid)
      setError(t('onboarding.location.fillAll'))
      scrollToFirstFieldError([...invalid])
      return
    }

    const registryFallbackMode = !coords && nearbyStores.some((store) => store.is_registry_fallback)
    if ((lookupState === 'error' || !coords) && !registryFallbackMode) {
      setInvalidFields(new Set(['street_address']))
      setError(lookupError || t('onboarding.location.lookup.invalidAddress'))
      scrollToFirstFieldError(['street_address'])
      return
    }

    if (nearbyStores.length === 0) {
      setInvalidFields(new Set(['preferred_retailer_id']))
      setError(t('onboarding.location.lookup.noNetworksAvailable'))
      scrollToFirstFieldError(['preferred_retailer_id'])
      return
    }

    setInvalidFields(new Set())
    onNext({
      ...formData,
      weekly_budget_value: parseFloat(formData.weekly_budget_value),
      user_lat: coords?.lat ?? null,
      user_lng: coords?.lng ?? null,
      nearby_stores: nearbyStores,
      country_code: countryCode,
      preferred_currency: locale.currency_code,
      currency_code: locale.currency_code,
      currency_symbol: locale.currency_symbol,
      preferred_retailer_id: selectedRetailerId,
      preferred_store_name: selectedChain!.name,
      timezone: clientTimezone(),
    })
  }

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) => {
    const { name, value } = e.target
    setFormData({ ...formData, [name]: value })
    setInvalidFields((prev) => {
      if (!prev.has(name)) return prev
      const next = new Set(prev)
      next.delete(name)
      return next
    })
  }

  return (
    <OnboardingLayout step={stepIndex} totalSteps={ONBOARDING_STEP_COUNT} onNext={handleSubmit} onPrev={onPrevious}>
      <form className="space-y-6">
        <div>
          <h2 className={ONBOARDING.heading}>{t('onboarding.location.heading')}</h2>
          <p className={ONBOARDING.subtitle}>{t('onboarding.location.subtitle')}</p>
        </div>

        <OnboardingField id="street_address" label={t('onboarding.location.streetAddress')} required invalid={invalidFields.has('street_address')}>
          <input
            type="text"
            name="street_address"
            value={formData.street_address}
            onChange={handleChange}
            className={onboardingFieldControlClass(ONBOARDING.input, invalidFields.has('street_address'))}
            placeholder={t('onboarding.location.streetPlaceholder')}
          />
        </OnboardingField>

        <div className="grid gap-4 sm:grid-cols-2">
          <OnboardingField id="zipcode" label={t('onboarding.location.postalCode')} required invalid={invalidFields.has('zipcode')}>
            <input
              type="text"
              name="zipcode"
              value={formData.zipcode}
              onChange={handleChange}
              className={onboardingFieldControlClass(ONBOARDING.input, invalidFields.has('zipcode'))}
              placeholder={t('onboarding.location.postalPlaceholder')}
            />
          </OnboardingField>
          <OnboardingField id="city" label={t('onboarding.location.city')} required invalid={invalidFields.has('city')}>
            <input
              type="text"
              name="city"
              value={formData.city}
              onChange={handleChange}
              className={onboardingFieldControlClass(ONBOARDING.input, invalidFields.has('city'))}
              placeholder={t('onboarding.location.cityPlaceholder')}
            />
          </OnboardingField>
        </div>

        {lookupState === 'loading' ? (
          <p className={`text-sm ${ONBOARDING.muted}`}>{t('onboarding.location.lookup.loading')}</p>
        ) : null}

        {lookupState === 'success' && countryCode ? (
          <p className={`text-xs font-mono uppercase tracking-widest ${ONBOARDING.mutedSoft}`}>
            {t('onboarding.location.regionDetected', {
              country: countryCode,
              symbol: locale.currency_symbol,
              code: locale.currency_code,
            })}
          </p>
        ) : null}

        {retailChains.length > 0 ? (
          <OnboardingField id="preferred_retailer_id" label={t('onboarding.location.preferredRetailer')} required invalid={invalidFields.has('preferred_retailer_id')}>
            <div className="grid gap-3 sm:grid-cols-2">
              {retailChains.map((chain) => {
                const active = selectedRetailerId === chain.id
                return (
                  <button
                    key={chain.id}
                    type="button"
                    onClick={() => {
                      setSelectedRetailerId(chain.id)
                      setInvalidFields((prev) => {
                        if (!prev.has('preferred_retailer_id')) return prev
                        const next = new Set(prev)
                        next.delete('preferred_retailer_id')
                        return next
                      })
                    }}
                    className={onboardingChoiceClass(active)}
                  >
                    <span className="mr-2">{chainIcon(chain.brand_key)}</span>
                    <span className={`font-semibold ${ONBOARDING.reviewValue}`}>{chain.name}</span>
                    <p className={`mt-1 text-xs ${ONBOARDING.muted}`}>
                      {t('onboarding.location.networkLabel', { country: chain.country_code })}
                    </p>
                  </button>
                )
              })}
            </div>
          </OnboardingField>
        ) : null}

        {lookupState === 'success' && mappedStoreCount > 0 ? (
          <div className={ONBOARDING.infoSuccess}>
            <p className={`text-sm font-medium ${ONBOARDING.reviewValue}`}>
              {t('onboarding.location.lookup.foundNearby', { count: mappedStoreCount })}
            </p>
            <ul className="mt-2 space-y-1.5">
              {nearbyStores
                .filter((store) => !store.is_registry_fallback)
                .map((store) => (
                  <li key={store.place_id ?? `${store.chain}-${store.address}`} className={`text-xs ${ONBOARDING.muted}`}>
                    <span className="mr-1">{chainIcon(store.brand_key ?? store.chain.toLowerCase())}</span>
                    <span className={`font-medium ${ONBOARDING.reviewValue}`}>{store.name}</span>
                    <span className={ONBOARDING.mutedSoft}> · {store.distance_km}km · {store.address}</span>
                  </li>
                ))}
            </ul>
          </div>
        ) : null}

        {lookupError ? (
          <div className={ONBOARDING.infoWarning}>{lookupError}</div>
        ) : null}

        <OnboardingField id="shopping_preference" label={t('onboarding.location.shoppingPreference')} required invalid={invalidFields.has('shopping_preference')}>
          <select
            name="shopping_preference"
            value={formData.shopping_preference}
            onChange={handleChange}
            className={onboardingFieldControlClass(ONBOARDING.select, invalidFields.has('shopping_preference'))}
          >
            <option value="">{t('onboarding.form.select')}</option>
            <option value="in-store">{t('onboarding.location.shopInStore')}</option>
            <option value="delivery">{t('onboarding.location.shopDelivery')}</option>
            <option value="both">{t('onboarding.location.shopBoth')}</option>
          </select>
        </OnboardingField>

        <OnboardingField id="weekly_budget_value" label={t('onboarding.location.weeklyBudget', { symbol: locale.currency_symbol })} required invalid={invalidFields.has('weekly_budget_value')}>
          <input
            type="number"
            name="weekly_budget_value"
            value={formData.weekly_budget_value}
            onChange={handleChange}
            className={onboardingFieldControlClass(ONBOARDING.input, invalidFields.has('weekly_budget_value'))}
            placeholder={budgetSuggestion ? String(budgetSuggestion) : t('onboarding.location.budgetPlaceholder')}
          />
          {budgetSuggestion && mealFrequency ? (
            <p className="mt-1 text-xs text-kilo-text-muted">
              {t('onboarding.location.budgetHint', {
                meals: mealFrequency === '3_meals' ? '3' : mealFrequency === '4_meals' ? '4' : '5+',
                symbol: locale.currency_symbol,
                amount: budgetSuggestion,
              })}
            </p>
          ) : null}
        </OnboardingField>

        {error && <div className={ONBOARDING.error}>{error}</div>}
      </form>
    </OnboardingLayout>
  )
}

export default Step6
